import { ArrowRight, Check, LoaderCircle, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';

import { cn } from '@/lib/utils';
import type { HintAnchorId } from '@/shared/stores';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { useHintAnchor } from '@/shared/ui/primitives';

import type { EnrichedProposedAction } from '../model';
import { PayloadOptionCard } from './PayloadOptionCard';

const TOOL_LABEL: Record<string, string> = {
  draft_prescription: 'Rx',
  draft_existing_lab: 'Lab',
  send_follow_up: 'Message',
  approve_refill: 'Refill',
  deny_refill: 'Refill',
  replace_refill: 'Refill',
  approve_rxchange: 'RxChange',
  approve_rxchange_with_changes: 'RxChange',
  deny_rxchange: 'RxChange',
};

interface ProposedActionChecklistRowProps {
  item: EnrichedProposedAction;
  index: number;
  onApprove?: (item: EnrichedProposedAction) => Promise<void> | void;
  onReject?: (item: EnrichedProposedAction, reason: string) => Promise<void> | void;
  onRevise?: (item: EnrichedProposedAction, reason: string) => Promise<void> | void;
  onExecute?: (item: EnrichedProposedAction) => void;
  onSwitchAlternative?: (externalId: string, alternativePayloadId: string) => void;
  isApproving?: boolean;
  isRejecting?: boolean;
  /** Set on the priority row only; engine spotlights the first registered. */
  hintAnchor?: HintAnchorId;
}

export function ProposedActionChecklistRow({
  item,
  index,
  onApprove,
  onReject,
  onRevise,
  onExecute,
  onSwitchAlternative,
  isApproving = false,
  isRejecting = false,
  hintAnchor,
}: ProposedActionChecklistRowProps) {
  const { action, visibility } = item;
  const isApproved = action.decision === 'Approved';
  const canExecute = visibility === 'executable' && action.decision === 'Approved';
  const isTerminal = visibility === 'succeeded' || visibility === 'failed';
  const hasDependencies = item.unmetDependencyExternalIds.length > 0;
  const canReview = action.decision === 'Pending' && !isTerminal;
  const canReject = (canReview || canExecute) && !isTerminal;
  const hasAlternatives = action.alternatives.length > 0;
  const hasJustification = action.justification.trim().length > 0;
  const toolLabel = TOOL_LABEL[action.payload.tool_name];

  const followUpMessageContent =
    action.payload.tool_name === 'send_follow_up' &&
    action.payload.tool_input != null &&
    typeof action.payload.tool_input === 'object' &&
    !Array.isArray(action.payload.tool_input) &&
    typeof action.payload.tool_input.content === 'string'
      ? action.payload.tool_input.content.trim()
      : '';

  const hasDetails =
    hasAlternatives ||
    hasJustification ||
    followUpMessageContent.length > 0 ||
    (visibility === 'failed' && !!action.run_error);

  const [expanded, setExpanded] = useState(false);
  const [alternativesDialogOpen, setAlternativesDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const hintRef = useHintAnchor(hintAnchor);

  const payloadOptions = useMemo(() => {
    const alternatives = action.alternatives.slice();
    const hasPayloadInAlternatives = alternatives.some(
      (option) => option.payload_id === action.payload.payload_id
    );
    return hasPayloadInAlternatives ? alternatives : [...alternatives, action.payload];
  }, [action.alternatives, action.payload]);

  const orderedPayloadOptions = useMemo(() => {
    return payloadOptions.slice().sort((a, b) => a.payload_id.localeCompare(b.payload_id));
  }, [payloadOptions]);

  const handleRejectSubmit = async (withRevision: boolean) => {
    const trimmedReason = rejectReason.trim();
    if (trimmedReason === '') return;
    if (withRevision) {
      if (!onRevise) return;
      await onRevise(item, trimmedReason);
    } else {
      if (!onReject) return;
      await onReject(item, trimmedReason);
    }
    setRejectDialogOpen(false);
    setRejectReason('');
  };

  let dotClassName = 'bg-muted-foreground/20';
  if (visibility === 'failed') {
    dotClassName = 'bg-destructive';
  } else if (visibility === 'running') {
    dotClassName = 'bg-emerald-500 animate-status-dot-pulse';
  } else if (visibility === 'succeeded') {
    dotClassName = 'bg-emerald-500/30';
  } else if (isApproved) {
    dotClassName = 'bg-emerald-500';
  }

  return (
    <motion.div
      ref={hintRef}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: index * 0.04, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div
        className={cn(
          'group flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors duration-150',
          hasDetails && !isTerminal && 'cursor-pointer',
          !isTerminal && 'hover:bg-muted/30',
          isTerminal && 'opacity-40'
        )}
        role={hasDetails && !isTerminal ? 'button' : undefined}
        tabIndex={hasDetails && !isTerminal ? 0 : undefined}
        onClick={() => {
          if (hasDetails && !isTerminal) {
            setExpanded((prev) => !prev);
          }
        }}
        onKeyDown={(e) => {
          if (hasDetails && !isTerminal && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
      >
        <div className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dotClassName)} />

        <span
          className={cn(
            'min-w-0 flex-1 truncate text-[11px] font-semibold',
            isTerminal
              ? 'text-muted-foreground line-through decoration-muted-foreground/30'
              : 'text-foreground'
          )}
        >
          {action.title}
        </span>

        <div
          className="flex shrink-0 items-center gap-1"
          onClick={(e) => {
            e.stopPropagation();
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
        >
          {canReject && onReject ? (
            <button
              type="button"
              className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground/60 transition-colors duration-150 hover:bg-destructive/10 hover:text-destructive"
              onClick={() => {
                setRejectDialogOpen(true);
              }}
              disabled={isApproving || isRejecting}
              aria-label={`Reject ${action.title}`}
            >
              <X className="size-3" strokeWidth={2} />
            </button>
          ) : null}

          {canReview && onApprove ? (
            <button
              type="button"
              className={cn(
                'flex h-5 w-5 items-center justify-center rounded transition-all duration-150',
                hasDependencies
                  ? 'cursor-not-allowed text-emerald-500/20'
                  : 'text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400'
              )}
              onClick={() => {
                onApprove(item);
              }}
              disabled={isApproving || isRejecting || hasDependencies}
              aria-label={`Approve ${action.title}`}
              title={hasDependencies ? 'Prerequisite actions must be completed first' : undefined}
            >
              {isApproving ? (
                <LoaderCircle className="size-3 animate-spin" />
              ) : (
                <Check className="size-3" strokeWidth={2.5} />
              )}
            </button>
          ) : null}

          {canExecute && onExecute ? (
            <button
              type="button"
              className="flex h-5 w-5 items-center justify-center rounded bg-primary text-primary-foreground transition-colors duration-150 hover:bg-primary/90"
              onClick={() => {
                onExecute(item);
              }}
              aria-label={`Execute ${action.title}`}
            >
              <ArrowRight className="size-3" strokeWidth={2.5} />
            </button>
          ) : null}
        </div>
      </div>

      {/* Inline expansion */}
      <AnimatePresence initial={false}>
        {expanded && hasDetails ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className="space-y-1.5 pb-2 pl-5 pr-1.5">
              {hasJustification ? (
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  {toolLabel ? `${toolLabel} \u2014 ` : ''}
                  {action.justification}
                </p>
              ) : null}

              {followUpMessageContent.length > 0 ? (
                <div className="rounded-md bg-muted/40 px-2.5 py-1.5">
                  <p className="mb-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                    Follow-up message
                  </p>
                  <p className="whitespace-pre-wrap break-words text-[11px] leading-relaxed text-foreground">
                    {followUpMessageContent}
                  </p>
                </div>
              ) : null}

              {hasAlternatives ? (
                <button
                  type="button"
                  className="text-[10px] font-medium text-primary transition-colors hover:text-primary/70"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAlternativesDialogOpen(true);
                  }}
                >
                  Compare {action.alternatives.length} alternative
                  {action.alternatives.length > 1 ? 's' : ''} &rarr;
                </button>
              ) : null}

              {visibility === 'failed' && action.run_error ? (
                <p className="text-[11px] leading-relaxed text-destructive">{action.run_error}</p>
              ) : null}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Alternatives dialog */}
      <Dialog open={alternativesDialogOpen} onOpenChange={setAlternativesDialogOpen}>
        <DialogContent className="max-w-lg gap-4 p-5">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold">{action.title}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Select the best option for this action.
            </DialogDescription>
          </DialogHeader>
          {hasAlternatives ? (
            <div className="overflow-x-auto pb-2">
              <div className="flex w-max items-start gap-2.5">
                {orderedPayloadOptions.map((option) => (
                  <PayloadOptionCard
                    key={option.payload_id}
                    payload={option}
                    isSelected={option.payload_id === action.payload.payload_id}
                    onSelect={
                      onSwitchAlternative && option.payload_id !== action.payload.payload_id
                        ? () => onSwitchAlternative(action.external_id, option.payload_id)
                        : undefined
                    }
                  />
                ))}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog
        open={rejectDialogOpen}
        onOpenChange={(open) => {
          setRejectDialogOpen(open);
          if (!open) {
            setRejectReason('');
          }
        }}
      >
        <DialogContent className="max-w-md gap-4 p-5">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold">Reject action</DialogTitle>
            <DialogDescription className="text-xs leading-relaxed text-muted-foreground">
              Share the reason for rejecting &ldquo;{action.title}&rdquo; so it is recorded.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            value={rejectReason}
            onChange={(e) => {
              setRejectReason(e.currentTarget.value);
            }}
            placeholder="Why are you rejecting this action?"
            className="min-h-20 text-sm"
          />

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={isRejecting}
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={isRejecting || rejectReason.trim() === ''}
              onClick={() => {
                void handleRejectSubmit(false);
              }}
            >
              {isRejecting ? 'Rejecting\u2026' : 'Reject'}
            </Button>
            {canReject && onRevise ? (
              <Button
                type="button"
                size="sm"
                className={cn(
                  'text-white',
                  hasDependencies
                    ? 'cursor-not-allowed bg-orange-600/40'
                    : 'bg-orange-600 hover:bg-orange-600/90'
                )}
                disabled={isRejecting || rejectReason.trim() === '' || hasDependencies}
                title={hasDependencies ? 'Prerequisite actions must be completed first' : undefined}
                onClick={() => {
                  void handleRejectSubmit(true);
                }}
              >
                Reject + revise
              </Button>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
